import React from "react";
import { Avatar, Box, Typography } from "@mui/material";
import i18n from "../../i18n";
import { useTranslation } from "react-i18next";
import type {
  ProgramRankingExportData,
  ProgramRankingPdfRow,
} from "../programRanking/types";
import type { MetricsStreamingFormat } from "../miContenidoExportData";
import { fmtCompactPlatform, fmtLiveMetric } from "./buildPeriodSummaryData";
import { metricsStreamingLabels } from "./metricsStreamingLabels";
import {
  rankCardsPageLayout,
  type RankCardsDensity,
} from "../rankCardsPageLayout";
import {
  REPORT_WEB,
  reportWebCaptionSx,
  reportWebCardSx,
  reportWebPlatformRowLabelSx,
  tabularNumsSx,
} from "./reportWebTheme";
import {
  getRankingPodscopeCaptionParts,
  formatRankingPodscopeMissingRankExplainer,
} from "../metricsPdf/formatHelpers";

const METRIC_LABEL_LINE_HEIGHT = 1.2;
const NAME_LINE_HEIGHT = 1.1;

type DensityTokens = {
  cardPaddingPx: number;
  identityGapPx: number;
  identityToMetricsGapPx: number;
  avatarSize: number;
  avatarRadius: number;
  avatarInitialsSize: number;
  rankFontSize: number;
  nameFontSize: number;
  nameLineCount: number;
  metricsPaddingTopPx: number;
  metricsMarginTopPx: number;
  metricRowGapPx: number;
  metricLabelFontSize: number;
  metricLabelMarginBottomPx: number;
  metricValueFontSize: number;
  captionMarginTop: number;
  gridGap: number;
  forceSingleLineLabels: boolean;
};

const DENSITY_TOKENS: Record<RankCardsDensity, DensityTokens> = {
  comfortable: {
    cardPaddingPx: 24,
    identityGapPx: 12,
    identityToMetricsGapPx: 14,
    avatarSize: 52,
    avatarRadius: 8,
    avatarInitialsSize: 15,
    rankFontSize: 22,
    nameFontSize: 15,
    nameLineCount: 3,
    metricsPaddingTopPx: 12,
    metricsMarginTopPx: 14,
    metricRowGapPx: 12,
    metricLabelFontSize: 8,
    metricLabelMarginBottomPx: 3,
    metricValueFontSize: 15,
    captionMarginTop: 1.75,
    gridGap: 2,
    forceSingleLineLabels: false,
  },
  compact: {
    cardPaddingPx: 10,
    identityGapPx: 8,
    identityToMetricsGapPx: 8,
    avatarSize: 32,
    avatarRadius: 6,
    avatarInitialsSize: 11,
    rankFontSize: 16,
    nameFontSize: 11,
    nameLineCount: 2,
    metricsPaddingTopPx: 8,
    metricsMarginTopPx: 8,
    metricRowGapPx: 6,
    metricLabelFontSize: 7,
    metricLabelMarginBottomPx: 2,
    metricValueFontSize: 11,
    captionMarginTop: 1.25,
    gridGap: 1.25,
    forceSingleLineLabels: true,
  },
};

function promAvgLabelLines(
  format: MetricsStreamingFormat,
  singleLine: boolean,
): string | readonly [string] | readonly [string, string] {
  const promAvg = metricsStreamingLabels(format).promAvg;
  if (singleLine) {
    return promAvg.toUpperCase();
  }
  if (promAvg.startsWith("Prom. ")) {
    return [i18n.t("metricsPdf:promPrefix"), promAvg.slice("Prom. ".length).toUpperCase()];
  }
  return [promAvg.toUpperCase()];
}

function rankingPodscopeLabel(
  singleLine: boolean,
): string | readonly [string, string] {
  return singleLine
    ? i18n.t("metricsPdf:rankPodscopeSingle")
    : [i18n.t("metricsPdf:rankLine1"), i18n.t("metricsPdf:rankLine2")];
}

const AVATAR_PALETTE = [
  "#2b5f8a",
  "#8a2b4f",
  "#2b8a5f",
  "#6a4b8a",
  "#8a6a2b",
  "#8a4f2b",
] as const;

const STOP_WORDS = new Set([
  "el",
  "la",
  "los",
  "las",
  "de",
  "del",
  "en",
  "y",
  "a",
  "un",
  "una",
]);

function programInitials(name: string): string {
  const words = name
    .trim()
    .split(/\s+/)
    .filter((w) => !STOP_WORDS.has(w.toLowerCase()));
  if (words.length >= 2) {
    return `${words[0]![0] ?? ""}${words[1]![0] ?? ""}`.toUpperCase();
  }
  return (words[0] ?? name).slice(0, 2).toUpperCase();
}

function programAvatarColor(programId: string): string {
  let hash = 0;
  for (let i = 0; i < programId.length; i++) {
    hash = (hash * 31 + programId.charCodeAt(i)) >>> 0;
  }
  return AVATAR_PALETTE[hash % AVATAR_PALETTE.length]!;
}

function ProgramAvatar({
  row,
  tokens,
}: {
  row: ProgramRankingPdfRow;
  tokens: DensityTokens;
}) {
  const avatarSx = {
    width: tokens.avatarSize,
    height: tokens.avatarSize,
    borderRadius: `${tokens.avatarRadius}px`,
    flexShrink: 0,
  } as const;

  if (row.imageUrl) {
    return (
      <Avatar
        src={row.imageUrl}
        alt=""
        variant="rounded"
        sx={{
          ...avatarSx,
          bgcolor: programAvatarColor(row.programId),
          fontSize: tokens.avatarInitialsSize,
          fontWeight: 700,
        }}
      >
        {programInitials(row.name)}
      </Avatar>
    );
  }

  return (
    <Box
      sx={{
        ...avatarSx,
        bgcolor: programAvatarColor(row.programId),
        color: "#fff",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: tokens.avatarInitialsSize,
        fontWeight: 700,
      }}
    >
      {programInitials(row.name)}
    </Box>
  );
}

function MetricBlock({
  label,
  value,
  align = "left",
  tokens,
}: {
  label: string | readonly [string] | readonly [string, string];
  value: string;
  align?: "left" | "right";
  tokens: DensityTokens;
}) {
  const lines: string[] = Array.isArray(label) ? [...label] : [label];
  const twoLine = lines.length > 1;
  const labelOneLineH = `${tokens.metricLabelFontSize * METRIC_LABEL_LINE_HEIGHT}px`;
  const labelTwoLineH = `${tokens.metricLabelFontSize * METRIC_LABEL_LINE_HEIGHT * 2}px`;

  return (
    <Box sx={{ minWidth: 0, textAlign: align }}>
      <Box
        sx={{
          ...reportWebPlatformRowLabelSx,
          fontSize: tokens.metricLabelFontSize,
          letterSpacing: "0.04em",
          lineHeight: METRIC_LABEL_LINE_HEIGHT,
          height: twoLine ? labelTwoLineH : labelOneLineH,
          mb: `${tokens.metricLabelMarginBottomPx}px`,
          textTransform: "none",
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-start",
          alignItems: align === "right" ? "flex-end" : "flex-start",
        }}
      >
        {lines.map((line) => (
          <Box
            key={line}
            component="span"
            sx={{ whiteSpace: "nowrap", textAlign: align }}
          >
            {line}
          </Box>
        ))}
      </Box>
      <Typography
        sx={{
          ...tabularNumsSx,
          fontSize: tokens.metricValueFontSize,
          fontWeight: 700,
          color: REPORT_WEB.brandPrimary,
          letterSpacing: "-0.02em",
          lineHeight: 1.1,
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}

function ProgramRankCard({
  row,
  periodStreamingFormat,
  tokens,
}: {
  row: ProgramRankingPdfRow;
  periodStreamingFormat: MetricsStreamingFormat;
  tokens: DensityTokens;
}) {
  const { t } = useTranslation("metricsPdf");
  const promLabel = promAvgLabelLines(
    row.streamingFormat ?? periodStreamingFormat,
    tokens.forceSingleLineLabels,
  );
  const nameBoxHeight =
    tokens.nameFontSize * NAME_LINE_HEIGHT * tokens.nameLineCount;

  return (
    <Box
      sx={{
        ...reportWebCardSx,
        p: `${tokens.cardPaddingPx}px`,
        minWidth: 0,
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        gap: `${tokens.identityToMetricsGapPx}px`,
        boxSizing: "border-box",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: `${tokens.identityGapPx}px`,
          minWidth: 0,
        }}
      >
        <Typography
          sx={{
            ...tabularNumsSx,
            fontSize: tokens.rankFontSize,
            fontWeight: 700,
            color: REPORT_WEB.brandPrimary,
            letterSpacing: "-0.03em",
            lineHeight: 1,
            flexShrink: 0,
          }}
        >
          #{row.rank}
        </Typography>
        <ProgramAvatar row={row} tokens={tokens} />
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            height: nameBoxHeight,
            display: "flex",
            alignItems: "center",
          }}
        >
          <Typography
            sx={{
              width: "100%",
              fontSize: tokens.nameFontSize,
              fontWeight: 700,
              color: REPORT_WEB.brandPrimary,
              lineHeight: NAME_LINE_HEIGHT,
              display: "-webkit-box",
              WebkitLineClamp: tokens.nameLineCount,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              overflowWrap: "normal",
              wordBreak: "normal",
              hyphens: "none",
            }}
          >
            {row.name}
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          rowGap: `${tokens.metricRowGapPx}px`,
          columnGap: 1.5,
          pt: `${tokens.metricsPaddingTopPx}px`,
          mt: `${tokens.metricsMarginTopPx}px`,
          borderTop: `1px solid ${REPORT_WEB.rowDivider}`,
        }}
      >
        <MetricBlock
          label={t("viewsCaps")}
          value={row.totalViews > 0 ? fmtCompactPlatform(row.totalViews) : "—"}
          tokens={tokens}
        />
        <MetricBlock
          label={t("viewsAvgLabel")}
          value={row.avgViews > 0 ? fmtCompactPlatform(row.avgViews) : "—"}
          align="right"
          tokens={tokens}
        />
        <MetricBlock
          label={t("intLabel")}
          value={
            row.totalInteractions > 0
              ? fmtCompactPlatform(row.totalInteractions)
              : "—"
          }
          tokens={tokens}
        />
        <MetricBlock
          label={t("intAvgLabel")}
          value={
            row.avgInteractions > 0
              ? fmtCompactPlatform(row.avgInteractions)
              : "—"
          }
          align="right"
          tokens={tokens}
        />
        <MetricBlock
          label={promLabel}
          value={fmtLiveMetric(row.avgViewers)}
          tokens={tokens}
        />
        <MetricBlock
          label={rankingPodscopeLabel(tokens.forceSingleLineLabels)}
          value={
            row.rankPodscope != null && row.rankPodscope > 0
              ? `#${row.rankPodscope}`
              : "—"
          }
          align="right"
          tokens={tokens}
        />
      </Box>
    </Box>
  );
}

type Props = {
  /** Already sorted + ranked rows for this summary slide (do not re-sort). */
  rows: ProgramRankingPdfRow[];
  rankingPeriod?: { startDate: string; endDate: string } | null;
  podscopeActiveProgramCount?: number | null;
  streamingFormat?: MetricsStreamingFormat;
};

export function ReportWebProgramRankCards({
  rows,
  rankingPeriod = null,
  podscopeActiveProgramCount = null,
  streamingFormat = "live",
}: Props) {
  if (rows.length === 0) return null;

  const { cols, density } = rankCardsPageLayout(rows.length);
  const tokens = DENSITY_TOKENS[density];

  const { title, body } = getRankingPodscopeCaptionParts({
    rankingPeriod,
    podscopeActiveProgramCount,
  });
  const showMissingRankExplainer = rows.some(
    (row) => row.rankPodscope == null || row.rankPodscope <= 0,
  );

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        width: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: "grid",
          gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
          gridAutoRows: "1fr",
          gap: tokens.gridGap,
          alignItems: "stretch",
        }}
      >
        {rows.map((row) => (
          <ProgramRankCard
            key={row.programId}
            row={row}
            periodStreamingFormat={streamingFormat}
            tokens={tokens}
          />
        ))}
      </Box>
      <Box
        sx={{
          mt: tokens.captionMarginTop,
          flexShrink: 0,
          display: "flex",
          flexDirection: "column",
          gap: 0.25,
        }}
      >
        <Typography
          sx={{
            ...reportWebCaptionSx,
            fontSize: 11,
            lineHeight: 1.4,
            color: REPORT_WEB.textMutedAlt,
          }}
        >
          <Box component="span" sx={{ fontWeight: 700 }}>
            * {title}:{" "}
          </Box>
          {body}
        </Typography>
        {showMissingRankExplainer ? (
          <Typography
            sx={{
              ...reportWebCaptionSx,
              fontSize: 11,
              lineHeight: 1.4,
              color: REPORT_WEB.textMutedAlt,
            }}
          >
            <Box component="span" sx={{ fontWeight: 700 }}>
              —:{" "}
            </Box>
            {formatRankingPodscopeMissingRankExplainer(rankingPeriod)}
          </Typography>
        ) : null}
      </Box>
    </Box>
  );
}

export function programRankCardsHasData(
  ranking?: ProgramRankingExportData | null,
): boolean {
  return (ranking?.videos.length ?? 0) > 0;
}
